import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trash2, PanelRightClose, ChevronDown, Monitor, Smartphone } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";

export interface Comment {
  id: string;
  author: string;
  initials: string;
  content: string;
  timestamp: string;
  rawTimestamp?: string;
  positionX?: number;
  positionY?: number;
  authorId?: string;
  stakeholderToken?: string;
  deviceType?: 'desktop' | 'mobile';
}

interface CommentsPanelProps {
  comments?: Comment[];
  highlightedCommentId?: string | null;
  onCommentHover?: (commentId: string | null) => void;
  onCommentClick?: (commentId: string) => void;
  onAddComment?: (content: string) => void;
  onDeleteComment?: (commentId: string) => void;
  currentUserId?: string;
  stakeholderToken?: string;
  isReadOnly?: boolean;
  isCollapsible?: boolean;
  onToggle?: () => void;
  isMobileExpanded?: boolean;
  variationTag?: string | null;
}

const CommentsPanel = ({ 
  comments = [], 
  highlightedCommentId,
  onCommentHover, 
  onCommentClick, 
  onAddComment, 
  onDeleteComment, 
  currentUserId, 
  stakeholderToken, 
  isReadOnly = false,
  isCollapsible = false,
  onToggle,
  isMobileExpanded = false,
  variationTag = null
}: CommentsPanelProps) => {
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const commentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const handlePostComment = () => {
    if (newComment.trim()) {
      onAddComment?.(newComment.trim());
      setNewComment("");
    }
  };

  const setCommentRef = (id: string, el: HTMLDivElement | null) => {
    if (el) {
      commentRefs.current.set(id, el);
    } else {
      commentRefs.current.delete(id);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-card z-10 -mx-6 px-6 -mt-6 pt-6 pb-4">
        <div className="flex flex-col">
          <h3 className="font-display font-bold text-foreground text-lg">Comments</h3>
          {variationTag && (
            <p className="text-xs text-muted-foreground mt-0.5">Variation: {variationTag}</p>
          )}
        </div>
        {isCollapsible && onToggle && (
          <button 
            onClick={onToggle} 
            className="p-1.5 hover:bg-muted rounded-md transition-colors"
            title="Collapse comments"
          >
            {isMobileExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <PanelRightClose className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        )}
      </div>
      
      {/* Comments List */}
      <div className="space-y-6 pr-2">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground mb-1">No comments yet</p>
            <p className="text-xs text-muted-foreground/70">Click anywhere on the email to leave feedback</p>
          </div>
        ) : (
          comments.map(comment => {
            const isHovered = hoveredCommentId === comment.id;
            const isHighlighted = highlightedCommentId === comment.id;
            const hasPosition = comment.positionX !== undefined && comment.positionY !== undefined;
            
            return (
              <div 
                key={comment.id}
                ref={(el) => setCommentRef(comment.id, el)}
                data-comment={comment.id}
                className={cn(
                  "rounded-xl p-3 transition-all duration-300 group",
                  isHighlighted 
                    ? "bg-acid-green/30 ring-2 ring-acid-green/50" 
                    : isHovered 
                      ? "bg-acid-green/20" 
                      : "bg-muted/30",
                  hasPosition && "cursor-pointer"
                )}
                onMouseEnter={() => {
                  if (hasPosition) {
                    setHoveredCommentId(comment.id);
                    onCommentHover?.(comment.id);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredCommentId(null);
                  onCommentHover?.(null);
                }}
                onClick={() => {
                  if (hasPosition) {
                    onCommentClick?.(comment.id);
                  }
                }}
              >
                {/* Author */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1.5">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground">
                      {comment.initials}
                    </div>
                    <span className="font-medium text-xs text-foreground">{comment.author}</span>
                    <span className="text-[10px] text-muted-foreground hidden sm:inline">•</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[10px] text-muted-foreground">
                            {comment.timestamp}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          {comment.rawTimestamp 
                            ? format(new Date(comment.rawTimestamp), "MMM d, yyyy 'at' h:mm a")
                            : comment.timestamp}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {/* Device type indicator */}
                    {comment.deviceType && (
                      <span 
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-muted text-muted-foreground"
                        title={comment.deviceType === 'mobile' ? 'Mobile view' : 'Desktop view'}
                      >
                        {comment.deviceType === 'mobile' ? (
                          <Smartphone className="w-2.5 h-2.5" />
                        ) : (
                          <Monitor className="w-2.5 h-2.5" />
                        )}
                      </span>
                    )}
                  </div>
                  {(comment.authorId === currentUserId || (stakeholderToken && comment.stakeholderToken === stakeholderToken)) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCommentToDelete(comment.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1"
                      title="Delete comment"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              
              {/* Content */}
              <p className="text-xs text-foreground mb-2 leading-relaxed whitespace-pre-wrap break-words">
                {comment.content}
              </p>
              
              </div>
            );
          })
        )}
      </div>
      
      {/* Add Comment Input */}
      <div className="pt-4 bg-card -mx-1 px-1">
        {isReadOnly ? (
          <div className="bg-muted/30 border border-foreground/10 rounded-xl p-4 text-center">
            <p className="text-sm text-muted-foreground">
              Viewing an older version. Comments can only be added to the latest version.
            </p>
          </div>
        ) : (
          <>
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="w-full bg-muted/30 border border-foreground/10 rounded-xl p-4 text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[100px]"
            />
            <div className="flex justify-end mt-3">
              <Button onClick={handlePostComment} variant="outline" className="rounded-lg px-6">
                POST COMMENT
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your comment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (commentToDelete) {
                  onDeleteComment?.(commentToDelete);
                  setCommentToDelete(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommentsPanel;
